import type { InvoiceExtraction, Evidence } from '@/types/invoiceAnalyser'
import { useT } from '@/i18n/useT'
import {
  Button,
  CenterModal,
  ModalHeader,
  SectionLabel,
  WarningBlock,
} from '@/components/ui'

interface EvidenceDialogProps {
  open: boolean
  onClose: () => void
  extraction: InvoiceExtraction | null
  /** When set, that field's snippet is pulled to the top and highlighted */
  focusField?: keyof Evidence | null
}

// English source strings — the same keys the extraction card uses, so a field
// is called the same thing in the card and in its audit trail.
const FIELD_LABELS: { [K in keyof Evidence]: string } = {
  supplier: 'Supplier',
  invoice_date: 'Invoice date',
  invoice_number: 'Invoice number',
  currency: 'Currency',
  total_net: 'Net',
  total_gross: 'Gross',
  vat_amount: 'VAT',
  tax_free_amount: 'Tax-free',
  taxable_net_amount: 'Taxable net',
  project_name: 'Project',
  recipient: 'Recipient',
}

const FIELD_ORDER: Array<keyof Evidence> = [
  'supplier',
  'invoice_number',
  'invoice_date',
  'currency',
  'total_net',
  'vat_amount',
  'tax_free_amount',
  'taxable_net_amount',
  'total_gross',
  'recipient',
  'project_name',
]

/** The extracted value for a field, as text. */
function valueOf(extraction: InvoiceExtraction, field: keyof Evidence): string | null {
  const raw = extraction[field as keyof InvoiceExtraction]
  if (raw == null || raw === '') return null
  return typeof raw === 'number' ? String(raw) : String(raw)
}

/**
 * The audit trail: for every extracted field, the snippet the model read it
 * from. Follows the mockup's "Fundstelle im Dokument" pattern — a quoted
 * monospace block behind an accent rule.
 */
export function EvidenceDialog({ open, onClose, extraction, focusField }: EvidenceDialogProps) {
  const { t } = useT()
  if (!extraction) return null

  const { evidence, warnings } = extraction

  // The clicked field first, so the modal opens on what was asked about
  const fields = focusField
    ? [focusField, ...FIELD_ORDER.filter((f) => f !== focusField)]
    : FIELD_ORDER

  return (
    <CenterModal
      open={open}
      onClose={onClose}
      width={680}
      header={
        <ModalHeader
          title={
            focusField ? `${t('Audit trail')} — ${t(FIELD_LABELS[focusField])}` : t('Audit trail')
          }
          subtitle={`${t('Confidence')} ${t(extraction.confidence)} · ${t(
            '{n} of {total} fields with a source passage',
          )
            .replace('{n}', String(fields.filter((f) => evidence?.[f]).length))
            .replace('{total}', String(FIELD_ORDER.length))}`}
          onClose={onClose}
        />
      }
      footer={
        <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="accent" onClick={onClose}>
            {t('Close')}
          </Button>
        </div>
      }
    >
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {warnings && warnings.length > 0 && <WarningBlock items={warnings} />}

        {fields.map((field, i) => {
          const value = valueOf(extraction, field)
          const snippet = evidence?.[field] ?? null
          const focused = focusField === field

          return (
            <div key={field}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <SectionLabel style={{ flex: 1, color: focused ? 'var(--accent)' : 'var(--faint)' }}>
                  {t(FIELD_LABELS[field])}
                </SectionLabel>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: value ? 'var(--text)' : 'var(--faint)',
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                    textAlign: 'right',
                  }}
                >
                  {value ?? t('Not detected')}
                </div>
              </div>
              <div
                style={{
                  marginTop: 8,
                  padding: '12px 14px',
                  borderLeft: `3px solid ${snippet ? 'var(--accent)' : 'var(--line)'}`,
                  background: 'var(--field)',
                  borderRadius: '0 8px 8px 0',
                  fontFamily: 'var(--mono)',
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: snippet ? 'var(--text)' : 'var(--faint)',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                }}
              >
                {snippet ?? t('No passage found in the document — the value is not from the text.')}
              </div>
              {i === 0 && fields.length > 1 && (
                <div style={{ height: 1, background: 'var(--line)', marginTop: 16 }} />
              )}
            </div>
          )
        })}

        <div>
          <SectionLabel>{t('VAT ID')}</SectionLabel>
          <div style={{ fontSize: 13, marginTop: 6 }}>
            {extraction.ust_id || (
              <span style={{ color: 'var(--faint)' }}>
                {t('Not found in the document — can be filled in from the supplier whitelist.')}
              </span>
            )}
          </div>
        </div>
      </div>
    </CenterModal>
  )
}
